import React from 'react';
import { connect } from 'react-redux';
import { Grid, Row, Col } from 'react-bootstrap';
import { get, isEmpty } from 'lodash';

import { fetchMeta as fetchEntityMeta, fetchMetaSocial } from 'services/entities/duck';
import { fetchChannel, fetchChannelCreatives, fetchChannelCallouts, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchSocialCampaigns, fetchSocialAdSets, fetchSocialDemographics, fetchSocialActions } from 'services/social/duck';
import { fetchLayerTwoChannelSummary } from 'services/layerTwo/duck';
import { reduceLayerTwoData, reduceCreativesData, shouldShowTotalActivities } from 'helpers/data';
import { safeDivide } from 'helpers/utilities';
import Divider from 'components/atoms/Divider';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { stripJobNumber } from 'helpers/strings';

// Components and Containers
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import DonutChart from 'components/molecules/charts/DonutChart';
import ExpandedDonut from 'components/molecules/ExpandedDonut';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import ExportPDF from 'containers/buttons/ExportPDF';
import { objectiveMap as SocialContainerMap } from 'containers/dashboards/social/index';
import { objectives as objectiveNameMap, orderedObjectives } from 'constants/facebook';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';

// Charts
import SocialCampaignOverviewTable from 'components/molecules/tables/SocialCampaignOverviewTable';
import ProtectedDemographicsReports from 'components/organisms/ProtectedDemographicsReports';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';


// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';
import StackedBarChart from 'components/molecules/charts/StackedBarChart';
import styled from 'styled-components';
import DividerLine from 'components/atoms/DividerLine';


const DONUTCHARTMAX = 10;
const DONUTCHARTMIN = 1;
const showTopN = 10;

const DividerLineStyled = styled(DividerLine)`
    margin-top: 125px;
`;

class LayerThreeSocialContainer extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'social');
        this.state = {
            ...this.state,
            showConversionTrends: true
        };
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchMetaSocial(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannel(payload);
        this.props.fetchSocialAdSets(payload);
        this.props.fetchSocialCampaigns(payload);
        this.props.fetchSocialDemographics(payload);
        this.props.fetchSocialActions(payload);

        if (this.props.layer === LAYERTWO) {
            this.props.fetchLayerTwoChannelSummary(payload);
        }
    }

    render() {
        const {
            entityMeta,
            xAxisLabel,
            backfillBuckets,
            selectedTimeRange,
            layerTwo,
            entityObjective,
            entityObjectiveLabelCode,
            layer = LAYERTHREE,
            showConversionRevenue,
            showSocialCpconv,
            showFacebookDemographicData,
            showSocialExpandModules
        } = this.props;
        const {
            conversionTermPlural,
            conversionTermSingular,
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);


        const channel = this.getCurrentChannel();
        const stub = `l${layer}-${channel}`;
        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.social', false);
        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasDynamicCreativesData = get(this.props, 'channel.dynamicCreatives.params.hasData', null);
        const hasCreativesData = get(this.props, 'channel.creatives.params.hasData', null);
        const callouts = get(this.props, 'channel.callouts.data', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);
        const hasDemographicsData = get(this.props, 'demographics.hasData', false);
        const demographicsData = get(this.props, 'demographics.data', []);
        const demographicsParams = get(this.props, 'demographics.params', {});
        const actions = get(this.props, 'actions', {});
        const creatives = get(this.props, 'channel.creatives', {});
        const userId = get(this.props, 'currentUser.id', null);

        const hasCampaignsData = get(this.props, 'campaigns.hasData', false);
        const campaigns = get(this.props, 'campaigns', {});
        let reducedCampaignData = {};
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', false);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        if (hasCampaignsData) {
            const scopeKey = campaigns.scopeKey;
            reducedCampaignData = campaigns.rankings.actions.reduce((prev, d) => {
                const campaignName = stripJobNumber(campaigns.campaignIdMap[d.name].name);
                prev[campaignName] = campaigns[d.name][scopeKey];
                return prev;
            }, {});
        }

        let creativesData = null;
        let bucketPercentage;
        const creativesParams = get(this.props, 'channel.creatives.params', {});

        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives, { removeJobNumber: true });
        }

        const showCampaignCharts = !!(hasCampaignsData && Object.keys(reducedCampaignData).length > DONUTCHARTMIN);
        const showExpandedDonut = !!(hasCampaignsData && Object.keys(reducedCampaignData).length > DONUTCHARTMAX);
        const Donut = showExpandedDonut ? ExpandedDonut : DonutChart;
        const showObjectiveCharts = !(showExpandedDonut || !showCampaignCharts);

        const upsellSettings = entityMeta.upsellSettings;
        const upsellRequested = get(entityMeta, 'upsellRequested', {});

        const calloutSettings = [
            {
                key: 'impressions',
                title: 'Impressions',
                format: '0,0'
            },
        ];

        if (showSocialCpconv) {
            calloutSettings.push({
                key: 'cost_per_conversion',
                title: `Cost per ${conversionTermSingular}`,
                format: '$0.00'
            });
        }

        if (hasCalloutData) {
            if (callouts.current_conversions === 0) {
                calloutSettings.push({
                    key: 'clicks',
                    title: 'Clicks',
                    format: '0,0'
                });
                if (this.state.showConversionTrends) {
                    this.setState(prevState => ({ ...prevState, showConversionTrends: false }));
                }
            } else {
                calloutSettings.push({
                    key: 'conversions',
                    title: conversionTermPlural,
                    format: '0,0'
                });
            }
        }

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const socialObjectives = get(entityMeta, 'social.objectives', []);
        let socialObjectivesDisplay = null;
        if (socialObjectives.length) {
            // loop through each enabled objective
            const socialObjectivesMap = socialObjectives.reduce((prev, objective) => {
                // look up in the social container map for the enabled objective
                if (SocialContainerMap[objective]) {
                    const Component = SocialContainerMap[objective];

                    // create that component giving it the current entityId so it can retrieve data
                    // this is to prevent having to pass down the ReactRouter `match` props that are given
                    // to this container component.
                    prev[objective] = <Component key={objective}
                        layer={layer}
                        entityId={this.getCurrentEntityId()}
                    />;
                }

                // If we encounter an objective that we don't have a module for, do nothing
                return prev;
            }, {});

            socialObjectivesDisplay = orderedObjectives.reduce((pre, orderedObjective) => {
                const component = socialObjectivesMap[orderedObjective];
                if (component) {
                    pre.push(component);
                }
                return pre;
            }, []);
        }

        const totalBudget = get(this.props, 'campaigns.statistics.spend', 0);

        const rankedSpendByCampaign = hasCampaignsData ? campaigns.rankings.spend.reduce((prev, data) => {
            const campaignName = stripJobNumber(campaigns.campaignIdMap[data.name].name);
            if (data.value > 0) {
                prev.data.push({
                    name: campaignName,
                    y: safeDivide(data.value, totalBudget)
                });
            }
            return prev;
        }, { data: [], total: totalBudget }) : {};

        // calculate data-specific bucket percentage for donut charts
        if (!isEmpty(rankedSpendByCampaign)) {
            if (Object.keys(rankedSpendByCampaign.data).length > DONUTCHARTMAX) {
                bucketPercentage = (Object
                    .values(rankedSpendByCampaign.data)
                    .sort((a, b) => b.y - a.y)[DONUTCHARTMAX].y) * 100;
            }
        }

        const spendByObjective = hasCampaignsData ?
            Object.entries(campaigns.campaignIdMap).reduce((prev, data) => {
                const objectiveName = data[1].objective;
                const spend = campaigns[data[0]].statistics.spend;
                const spendPercentage = safeDivide(spend, prev.total);
                const objective = prev.dataMap[objectiveName];

                if (objective) {
                    objective.y += spendPercentage;
                } else {
                    prev.dataMap[objectiveName] = {
                        name: objectiveNameMap[objectiveName] || 'Other',
                        y: spendPercentage
                    };
                }

                return prev;
            }, { dataMap: {}, total: totalBudget }) : { dataMap: {} };

        const rankedSpendByObjective = Object.values(spendByObjective.dataMap).sort((a, b) => b.y - a.y);

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} expandSocial />}
        </React.Fragment>;

        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && layer === LAYERTWO);
        const showCampaignSummaryTable = !!(hasCampaignsData && layer === LAYERTHREE);

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Social
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs     */}
                <Row>
                    {calloutComponents}
                </Row>

                {hasCampaignsData && this.state.showConversionTrends ? (
                    <StackedBarChart
                        title={`${conversionTermSingular} TRENDS`}
                        exportName={`${stub}-summary-actions-by-campaign`}
                        data={reducedCampaignData}
                        y="conversions"
                        xAxisLabel={xAxisLabel}
                        yAxisLabel={conversionTermPlural}
                        backfillBuckets={backfillBuckets}
                        yTitle={conversionTermPlural}
                        animateDuration={2000}
                        xAxisType="datetime"
                        showTopN={showTopN} />
                ) : (
                    <RenderingTile height='400px' show={hasCampaignsData && this.state.showConversionTrends} />
                )}

                {/* Child Tables */}
                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName={`${stub}-table-performance`}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        showConversionRevenue={showConversionRevenue}
                        accessors={['impressions', 'clicks', 'cpc', 'ctr', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversion_rate', 'spend']}
                        showSocialCpconv={showSocialCpconv}
                        cacheKey={`${stub}-table-performance-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )}

                {showCampaignSummaryTable ? (
                    <SocialCampaignOverviewTable
                        title="Campaign Overview"
                        exportName={`${stub}-summary-campaign-overview`}
                        hasData={hasCampaignsData}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        showConversionRevenue={showConversionRevenue}
                        campaigns={campaigns}
                        showSocialCpconv={showSocialCpconv}
                        showConversionTrends={this.state.showConversionTrends}
                        cacheKey={`${stub}-summary-campaign-overview-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable} />
                )}

                <Row>
                    <Col md={showExpandedDonut ? 12 : 6}>
                        {hasCampaignsData && showCampaignCharts ? (
                            <Donut
                                title='Budget By Campaign'
                                exportName={`${stub}-budget-by-campaign`}
                                data={rankedSpendByCampaign.data}
                                y="campaigns"
                                hideLegendLine
                                total={totalBudget}
                                totalTitle="Total Budget"
                                totalFormat='$0,0'
                                bucketPercentage={bucketPercentage}
                                donutCaption={`Note: This chart shows the top ${DONUTCHARTMAX} campaigns and groups all other campaigns into the "Other" slice.`} />
                        ) : (
                            <RenderingTile height='465px' show={showCampaignCharts} />
                        )}
                    </Col>
                    <Col md={showExpandedDonut ? 12 : 6}>
                        {hasCampaignsData && showObjectiveCharts && showCampaignCharts ? (
                            <Donut
                                title='Budget By Objective'
                                exportName={`${stub}-budget-by-objective`}
                                data={rankedSpendByObjective}
                                y="objectives"
                                hideLegendLine
                                total={totalBudget}
                                totalTitle="Total Budget"
                                totalFormat='$0,0' />
                        ) : (
                            <RenderingTile height='465px' show={showObjectiveCharts} />
                        )}
                    </Col>
                </Row>

                {hasCreativesData &&
                    <ProtectedCreativesReports
                        exportName={`${stub}-creatives`}
                        smartTable={true}
                        cacheKey={`${stub}-ad-performance-${userId}-${this.getCurrentEntityId()}`}
                        showPlatform={true}
                        creativeNameOverride="Ad"
                        labelCodeOverride="Conversion"
                        hasCreativesData={hasCreativesData}
                        creativesData={creativesData}
                        creativesParams={creativesParams}
                        eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                        eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        showTotalActivities={false}
                        showConversionRevenue={showConversionRevenue}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['label', 'size', 'impressions', 'clicks', 'ctr'] :
                                undefined
                        }
                        channel="social"
                    />
                }

                {hasDynamicCreativesData &&
                    <ProtectedDynamicCreativesReports
                        exportName={`${stub}-dynamic-creatives`}
                        hasDynamicCreativesData={hasDynamicCreativesData}
                        dynamicCreatives={get(this.props, 'channel.dynamicCreatives', {})}
                        dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                        entityObjective={entityObjective}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                    />
                }

                {/* Showing all actions irrespective of objective */}
                <SocialActionOverviewTable
                    exportName={`${stub}-lead-generation-action-overview`}
                    actions={actions}
                    campaigns={campaigns}
                    conversionTermSingular={conversionTermSingular}
                    conversionTermPlural={conversionTermPlural}
                    cacheKey={`${stub}-lead-generation-action-overview-${userId}-${this.getCurrentEntityId()}`}
                />
                {(showFacebookDemographicData || showSocialExpandModules) &&
                    <React.Fragment>
                        <DividerLineStyled />
                        <Divider
                            text="The charts below are exclusive to Facebook and related ads (not fully compatible with all campaign objectives)"
                        />
                    </React.Fragment>
                }
                {showFacebookDemographicData &&
                    <ProtectedDemographicsReports
                        exportName={`${stub}-demographics`}
                        hasDemographicsData={hasDemographicsData}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        demographicsData={demographicsData}
                        demographicsParams={demographicsParams}
                        showSocialCpconv={showSocialCpconv}
                    />
                }

                {showSocialExpandModules ? (
                    socialObjectivesDisplay
                ) : (
                    <RenderingTile height="465px" show={showSocialExpandModules} />
                )}
            </Grid>
        ) : (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Social
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.social ? (
                    <ChannelUpsell
                        channel='social'
                        channelDisplay='Social'
                        icon='icon-social-fill'
                        successfulRequest={upsellRequested.social}
                        buttonClick={() => {
                            const entityId = this.getCurrentEntityId();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const entityId = this.getCurrentEntityId();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='social' />
                )}

            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    channel: state.channel.social,
    layerTwo: state.layerTwo,
    actions: state.social.facebook.actions,
    campaigns: state.social.facebook.campaigns,
    demographics: state.social.facebook.demographics,
    selectedTimeRange: state.config.selectedTimeRange,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    showSocialCpconv: get(state, 'entities.meta.meta.showSocialCpconv', false),
    showSocialExpandModules: get(state, 'entities.meta.meta.showSocialExpandModules', false),
    showFacebookDemographicData: get(state, 'entities.meta.meta.showFacebookDemographicData', false),
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchSocialAdSets,
    fetchMetaSocial,
    fetchChannel,
    fetchChannelCreatives,
    fetchChannelCallouts,
    fetchSocialActions,
    fetchSocialCampaigns,
    fetchSocialDemographics,
    requestChannelUpsell,
    showedChannelUpsell,
    fetchLayerTwoChannelSummary
})(LayerThreeSocialContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeSocialContainer.js